import { useCallback } from 'react';
import { useError } from '../contexts/ErrorContext';

/**
 * Custom hook for handling API operations with built-in error handling
 * @returns {Object} Object containing methods for API operations
 */
export const useApiHandler = () => {
  const { handleApiError, handleSuccess, setLoading } = useError();

  /**
   * Execute an API call with automatic error handling
   * @param {Function} apiCall - The API function to execute
   * @param {Object} options - Configuration options
   * @returns {Promise} Promise that resolves with the API response data
   */
  const executeApiCall = useCallback(async (apiCall, options = {}) => {
    const {
      showLoading = true,
      showSuccess = false,
      successMessage = 'Operation completed successfully',
      errorOptions = {}
    } = options;

    try {
      if (showLoading) {
        setLoading(true);
      }

      const response = await apiCall();
      
      if (showSuccess) {
        handleSuccess(successMessage);
      }

      return response;
    } catch (error) {
      handleApiError(error, errorOptions);
      throw error; // Re-throw to allow component-level handling if needed
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [handleApiError, handleSuccess, setLoading]);

  /**
   * Execute a form submission with validation error handling
   * @param {Function} apiCall - The API function to execute
   * @param {Object} options - Configuration options
   * @returns {Promise} Promise that resolves with the API response data
   */
  const executeFormSubmission = useCallback(async (apiCall, options = {}) => {
    const {
      showLoading = true,
      showSuccess = true,
      successMessage = 'Form submitted successfully',
      errorOptions = { showToast: false } // Don't show toast for form errors, handle in component
    } = options;

    try {
      if (showLoading) {
        setLoading(true);
      }

      const response = await apiCall();
      
      if (showSuccess) {
        handleSuccess(successMessage);
      }

      return response;
    } catch (error) {
      // For form submissions, we want to return the error details
      // so components can handle validation errors appropriately
      const { errorMessage, fieldErrors } = handleApiError(error, errorOptions);
      
      return {
        success: false,
        error: errorMessage,
        fieldErrors,
        originalError: error
      };
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [handleApiError, handleSuccess, setLoading]);

  /**
   * Execute a delete operation with confirmation
   * @param {Function} apiCall - The API function to execute
   * @param {Object} options - Configuration options
   * @returns {Promise} Promise that resolves with the API response data
   */
  const executeDelete = useCallback(async (apiCall, options = {}) => {
    const {
      showLoading = true,
      showSuccess = true,
      successMessage = 'Item deleted successfully',
      confirmMessage = 'Are you sure you want to delete this item?',
      errorOptions = {}
    } = options;

    // Show confirmation dialog
    if (!window.confirm(confirmMessage)) {
      return { cancelled: true };
    }

    try {
      if (showLoading) {
        setLoading(true);
      }

      const response = await apiCall();
      
      if (showSuccess) {
        handleSuccess(successMessage);
      }

      return response;
    } catch (error) {
      handleApiError(error, errorOptions);
      throw error;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [handleApiError, handleSuccess, setLoading]);

  return {
    executeApiCall,
    executeFormSubmission,
    executeDelete,
    setLoading,
    handleSuccess,
    handleApiError
  };
};
